import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, X } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { getErrorMessage } from '@/lib/api'
import { ensureUniqueCrewTemplateCode } from '@/lib/crew-template-code'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import crewsApi from '@/services/crews'
import { workersApi } from '@/services/workers'
import type { Crew, CrewTemplate } from '@/types'

export default function CrewsStaffPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const searchParams = new URLSearchParams(location.search)
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const crewId = searchParams.get('crew_id')
  const templateIdParam = searchParams.get('template_id')

  const [selectedTemplateId, setSelectedTemplateId] = useState(templateIdParam ?? '')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [active, setActive] = useState(true)
  const [memberDrafts, setMemberDrafts] = useState<Record<string, string>>({})
  const [editingCrew, setEditingCrew] = useState<Crew | null>(null)
  const codeManuallyEditedRef = useRef(false)

  const templatesQuery = useQuery({
    queryKey: ['crew-templates', serviceInstanceId],
    queryFn: () => crewsApi.listTemplates({ service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
  })

  const crewQuery = useQuery({
    queryKey: ['crew', crewId],
    queryFn: () => crewsApi.getCrew(crewId!),
    enabled: Boolean(crewId),
  })

  const crewsQuery = useQuery({
    queryKey: ['crews', serviceInstanceId],
    queryFn: () => crewsApi.listCrews({ service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
  })

  const selectedTemplate = useMemo(
    () => (templatesQuery.data ?? []).find((t) => String(t.id) === selectedTemplateId) ?? null,
    [templatesQuery.data, selectedTemplateId],
  )

  const workerSkillsQuery = useQuery({
    queryKey: ['worker-skills', serviceInstanceId, selectedTemplate?.job_category],
    queryFn: () => workersApi.getWorkerSkills({
      service_instance_id: serviceInstanceId ?? undefined,
      job_category: selectedTemplate?.job_category ?? undefined,
      is_active: true,
    }),
    enabled: Boolean(serviceInstanceId && selectedTemplate),
  })

  const existingCrewCodes = useMemo(
    () => (crewsQuery.data ?? []).map((c) => String(c.code ?? '').trim()).filter(Boolean),
    [crewsQuery.data],
  )

  const buildCrewCodeSource = (teamName: string) => {
    const prefix = String(selectedTemplate?.job_category_name ?? '').trim()
    const normalized = teamName.trim()
    return normalized ? (prefix ? `${prefix}_${normalized}` : normalized) : ''
  }

  const suggestCrewCode = (value: string, excludeCode?: string | null) =>
    ensureUniqueCrewTemplateCode(buildCrewCodeSource(value), existingCrewCodes, excludeCode)

  const handleNameChange = (value: string) => {
    setName(value)
    if (!codeManuallyEditedRef.current) {
      setCode(value.trim() ? suggestCrewCode(value, editingCrew?.code) : '')
    }
  }

  useEffect(() => {
    if (codeManuallyEditedRef.current) return
    setCode(name.trim() ? suggestCrewCode(name, editingCrew?.code) : '')
  }, [name, selectedTemplate?.job_category_name, editingCrew?.code])

  // Load crew when crewId is available
  useEffect(() => {
    const crew = crewQuery.data
    if (!crew) return
    codeManuallyEditedRef.current = true
    setEditingCrew(crew)
    setSelectedTemplateId(String(crew.crew_template))
    setName(crew.name)
    setCode(crew.code)
    setActive(crew.active ?? true)
    const drafts: Record<string, string> = {}
    for (const member of crew.members ?? []) {
      drafts[`${member.crew_template_slot}:${member.ordering}`] = String(member.worker_skill)
    }
    setMemberDrafts(drafts)
  }, [crewQuery.data])

  const goBack = () =>
    navigate(`/services/crews?service_instance_id=${serviceInstanceId}&tab=staffed`, { replace: true })

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!serviceInstanceId || !selectedTemplateId) throw new Error('Team and service instance are required')
      const resolvedCode = (code.trim() || suggestCrewCode(name.trim(), editingCrew?.code)).trim()
      if (!resolvedCode) throw new Error('Team code is required')
      const payload = {
        crew_template: selectedTemplateId,
        service_instance: serviceInstanceId,
        name: name.trim(),
        code: resolvedCode,
        active,
      }
      const crew = editingCrew?.id
        ? await crewsApi.updateCrew(editingCrew.id, payload)
        : await crewsApi.createCrew(payload)

      const existingMembersByKey = new Map(
        (editingCrew?.members ?? []).map((m) => [`${m.crew_template_slot}:${m.ordering}`, m]),
      )
      for (const slot of selectedTemplate?.slots ?? []) {
        const quantity = Number(slot.quantity ?? 1)
        for (let idx = 1; idx <= quantity; idx++) {
          const key = `${slot.id}:${idx}`
          const workerSkillId = memberDrafts[key]
          const existing = existingMembersByKey.get(key)
          if (!workerSkillId) {
            if (existing?.id) await crewsApi.deleteMember(existing.id)
            existingMembersByKey.delete(key)
            continue
          }
          if (existing?.id) {
            if (String(existing.worker_skill) !== workerSkillId)
              await crewsApi.updateMember(existing.id, { worker_skill: workerSkillId })
            existingMembersByKey.delete(key)
            continue
          }
          await crewsApi.createMember({
            crew: crew.id,
            crew_template_slot: slot.id!,
            worker_skill: workerSkillId,
            ordering: idx,
          })
        }
      }
      for (const leftover of existingMembersByKey.values()) {
        if (leftover.id) await crewsApi.deleteMember(leftover.id)
      }
      return crew
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crews'] })
      toast.success(editingCrew ? 'Team updated' : 'Team created')
      goBack()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const skillsForRole = (roleId: string | number) =>
    (workerSkillsQuery.data ?? []).filter((s) => String(s.skill_role) === String(roleId))

  const isLoading = crewId ? crewQuery.isLoading : templatesQuery.isLoading

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={goBack}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to teams
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold">{editingCrew ? 'Edit team' : 'New team'}</h1>
        {selectedTemplate && (
          <p className="text-sm text-muted-foreground mt-0.5">{selectedTemplate.name}</p>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-6 max-w-3xl">
          {/* Team metadata */}
          <Card>
            <CardHeader><CardTitle className="text-base">Team details</CardTitle></CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Team template</Label>
                <Select
                  value={selectedTemplateId}
                  onValueChange={setSelectedTemplateId}
                  disabled={Boolean(editingCrew)}
                >
                  <SelectTrigger><SelectValue placeholder="Select template" /></SelectTrigger>
                  <SelectContent>
                    {(templatesQuery.data ?? []).map((t: CrewTemplate) => (
                      <SelectItem key={String(t.id)} value={String(t.id)}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Team name</Label>
                <Input value={name} onChange={(e) => handleNameChange(e.target.value)} placeholder="North Zone AC Team" />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input
                  value={code}
                  onChange={(e) => { codeManuallyEditedRef.current = true; setCode(e.target.value) }}
                  placeholder="Auto-generated"
                />
              </div>
              <div className="flex items-center gap-2 pt-7">
                <Switch checked={active} onCheckedChange={setActive} />
                <Label>Active</Label>
              </div>
            </CardContent>
          </Card>

          {/* Slot assignment */}
          {selectedTemplate && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Assign workers to slots</CardTitle>
                  <Badge variant="outline">
                    {Object.values(memberDrafts).filter(Boolean).length} / {selectedTemplate.slots?.reduce((sum, s) => sum + Number(s.quantity ?? 1), 0) ?? 0} filled
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {(selectedTemplate.slots ?? []).map((slot) => {
                    const quantity = Number(slot.quantity ?? 1)
                    const roleSkills = skillsForRole(slot.skill_role)
                    const allEntries = Array.from({ length: quantity }, (_, i) => {
                      const key = `${slot.id}:${i + 1}`
                      return { key, ordering: i + 1, workerId: memberDrafts[key] ?? '' }
                    })
                    const assigned = allEntries.filter((e) => e.workerId)
                    const nextEmpty = allEntries.find((e) => !e.workerId)
                    return (
                      <div key={String(slot.id)} className="flex items-start gap-4 px-4 py-3">
                        <div className="w-40 shrink-0 pt-1">
                          <p className="text-sm font-medium leading-tight">{slot.slot_label || slot.skill_role_name}</p>
                          <p className="text-xs text-muted-foreground">{assigned.length}/{quantity} assigned</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 flex-1 pt-0.5">
                          {assigned.map(({ key, workerId }) => {
                            const skill = roleSkills.find((s) => String(s.id) === workerId)
                            return (
                              <span key={key} className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2.5 py-1 text-xs">
                                {skill?.worker_display_name ?? workerId}
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-destructive ml-0.5"
                                  onClick={() => setMemberDrafts((cur) => { const next = { ...cur }; delete next[key]; return next })}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            )
                          })}
                          {nextEmpty && assigned.length < quantity && (
                            <Select
                              value=""
                              onValueChange={(value) => setMemberDrafts((cur) => ({ ...cur, [nextEmpty.key]: value }))}
                            >
                              <SelectTrigger className="h-7 w-36 border-dashed text-xs text-muted-foreground">
                                <SelectValue placeholder="＋ Add worker" />
                              </SelectTrigger>
                              <SelectContent>
                                {roleSkills.map((skill) => (
                                  <SelectItem key={String(skill.id)} value={String(skill.id)}>
                                    {skill.worker_display_name ?? 'Worker'} · {slot.skill_role_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-3">
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name.trim() || !selectedTemplateId}>
              {saveMutation.isPending ? 'Saving…' : editingCrew ? 'Update team' : 'Create team'}
            </Button>
            <Button variant="outline" onClick={goBack}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  )
}
